import { useEffect, useState } from "react";
import { Button, Flex, Icon, Radio, RadioGroup, Stack, Text } from "@chakra-ui/react"
import {
	RiAddLine,
	RiArrowLeftLine,
	RiAtLine,
	RiMap2Line,
	RiMenLine,
	RiProfileLine,
	RiStore2Line,
	RiUser3Line,
	RiWomenLine
} from "react-icons/ri";
import { MdFace, MdList, MdOutlineAddIcCall } from 'react-icons/md';
import { SubmitHandler, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useHistory } from "react-router-dom";
import { v4 as uuid } from 'uuid';


import Header from "../../components/Header";
import { Input } from "../../components/Inputs/Input";
import NewPhoneClienteModal from "../../components/NewPhoneClienteModal";
import NewEndClienteModal from "../../components/NewEndClienteModal";
import EnderecoCadastro from "../../components/EnderecoCadastro";
import PhoneCadastro from "../../components/PhoneCadastro";
import api from "../../services/api";
import { Select } from "../../components/Inputs/InputSelect";
import { useAuth } from "../../hooks/auth";

type EmpresaData = {
	emp_cod: string;
}
type ClientFormData = {
  doc: string;
	nome: string;
	nome_fantasia?: string;
  inicio_atividades?: Date;
  classif?: string;
  insc_estadual?: string;
  email?: string;
	rg?: string;
	nasc?: Date;
}
type ClientData = {
  id: string;
  doc: string;
  nome: string;
  nome_fantasia: string;
  inicio_atividades: Date;
  classif: string;
  insc_estadual: string;
  email: string;
  cnpj: string;
  rg: string;
  nasc: Date;
  telefones: PhoneData[];
  enderecos: EnderecoData[];
  empresas: EmpresaData[];
}
type PhoneData = {
	id: string;
	numero: string;
	obs?: string;
	emp_cod: string;
}
type EnderecoData = {
	id:string;
	cep: string;
	uf: string;
	cid: string;
	cidcod: string;
	bai: string;
	endereco: string;
	num: string;
	comp?: string;
	ponto_ref?: string;
	emp_cod: string;
}
type ClassificacaoData = {
	value: number;
	label: string;
}

const clientFormSchema: yup.SchemaOf<ClientFormData> = yup.object().shape({
	doc: yup.string().required('Documento obrigatória.'),
	nome: yup.string().required('Nome obrigatório.'),
	nome_fantasia: yup.string(),
	inicio_atividades: yup.date(),
  classif: yup.string(),
	insc_estadual: yup.string(),
  email: yup.string().email('E-mail inválido.'),
	rg: yup.string(),
	nasc: yup.date()
});

const CadClient: React.FC = () => {
	const { company, token } = useAuth();
	const history = useHistory();

	const [pessoa, setPessoa] = useState('fisica');
	const [sexo, setSexo] = useState('masculino');
	const [phones, setPhones] = useState<PhoneData[]>([]);
	const [phoneModal, setPhoneModal] = useState<PhoneData>({} as PhoneData);
	const [enderecos, setEnderecos] = useState<EnderecoData[]>([]);
	const [endModal, setEndModal] = useState<EnderecoData>({} as EnderecoData);
	const [classificacoes, setClassificacoes] = useState<ClassificacaoData[]>([]);

	const [isNewTelClienteModal, setIsNewTelClienteModal] = useState(false);
	const [isNewEndClienteModal, setIsNewEndClienteModal] = useState(false);

	const { register, handleSubmit, formState, setValue, setFocus, reset } = useForm<ClientFormData>({
		resolver: yupResolver(clientFormSchema)
	});

	useEffect(() => {
		api.get('https://servicodados.ibge.gov.br/api/v2/cnae/divisoes')
			.then(response => {
				const auxClass = response.data.map((classif: any) => {
					return {
						value: Number(classif.id),
						label: classif.descricao,
					}
				});
				auxClass.unshift({ value: 0, label: '' });
				setClassificacoes(auxClass);
			})
	}, []);

	useEffect(() => {
		reset();
	}, [pessoa]);

	function handleMaskCpf(value: string) {
		let res = value;
		res = res.replace(/\D+/g, '');
		res = res.replace(/(\d{3})(\d)/, "$1.$2");
		res = res.replace(/(\d{3})(\d)/, "$1.$2");
		res = res.replace(/(\d{3})(\d{1,2})$/, "$1-$2");
		setValue('doc', res);
	};
	function handleMaskCnpj(value: string) {
		let res = value;
		res = res.replace(/\D+/g, '');
		res = res.replace(/^(\d{2})(\d)/, "$1.$2")
		res = res.replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")
		res = res.replace(/\.(\d{3})(\d)/, ".$1/$2")
		res = res.replace(/(\d{4})(\d)/, "$1-$2")
		setValue('doc', res);
	};

	function handleCloseNewTelClienteModal() {
		setIsNewTelClienteModal(false);
	};
	function handleCloseNewEndClienteModal() {
		setIsNewEndClienteModal(false);
	};

	async function handleSearchCnpj(cnpj: string) {
		const auxCnpj = cnpj.replace(/\D/g, '');

		delete api.defaults.headers.common['Authorization'];
		delete api.defaults.headers.common['company'];
		await api.get(`https://brasilapi.com.br/api/cnpj/v1/${auxCnpj}`)
			.then(response => {
				const { nome_fantasia, razao_social, data_inicio_atividade, cnae_fiscal } = response.data;
				setValue('nome', razao_social);
				setValue('nome_fantasia', nome_fantasia);
				setValue('inicio_atividades', data_inicio_atividade);
				setValue('classif', String(cnae_fiscal).substr(0, 2));
				setFocus('insc_estadual');
			});

		api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
		api.defaults.headers.common['company'] = company.id;
	};

	const handleSubmitCliente: SubmitHandler<ClientFormData> = async (data: ClientFormData) => {
		const auxData = {
      ...data,
      id: uuid(),
      telefones: phones,
      enderecos: enderecos,
      empresas: [{ emp_cod: company.id }],
    } as ClientData;

		await api.post('controle/client/store', {
			cliente: auxData
		}).then(response => {
			reset();
			history.push('/client');
		})
	};

	return (
		<Flex
			w="100vw"
			h="100vh"
			flexDir="column"
			overflowY="auto"
		>
			<Header isOpenCart={() => {}} />

			<Flex justify="center" align="center" flexDir="column" mt="70px">
				{/* HEADER */}
				<Flex
					w="100%"
					maxW="800px"
				>
					<Flex
						justify="flex-start"
						align="center"
						onClick={() => history.push('/client')}
					>
						<Button bg="transparent" p="0" _hover={{ background: "gray.100" }}>
							<Icon as={RiArrowLeftLine} fontSize={30} color="blue.500" />
						</Button>
					</Flex>

				  <Flex justify="center" align="center" flexDir="column" w="100%">
						<Text fontSize="25" color="gray.600">Novo Cliente</Text>
						<Text fontSize="20" color="gray.500">Cadastro de novo cliente</Text>
					</Flex>
				</Flex>

				{/* FORMULÁRIO */}
				<Flex
					as="form"
					flexDir="column"
					w="100%"
					maxW="800px"
					bg="gray.50"
					px="8"
					py="4"
					mt="6"
					borderRadius={8}
					onSubmit={handleSubmit(handleSubmitCliente)}
				>
					<Stack my="4" spacing="4">
						{/* PESSOA - FISICA / JURIDICA */}
						<Flex
							flexDir="column"
							pt="2"
						>
							<Flex align="center">
								<Icon as={pessoa === 'fisica' ? MdFace : RiStore2Line} fontSize="18" mr="2" />
								<Text fontSize="18">Pessoa: </Text>
							</Flex>

							<RadioGroup
								onChange={setPessoa}
								value={pessoa}
								bg="gray.100"
								borderRadius={8}
								pl="4"
								py="2.5"
							>
								<Stack direction='row' spacing="4">
									<Radio value='fisica'>Física</Radio>
									<Radio value='juridica'>Jurídica</Radio>
								</Stack>
							</RadioGroup>
						</Flex>

						{/* DOCUMENTO */}
						<Input
							type="text"
							label={pessoa === 'fisica' ? 'Cpf' : 'Cnpj'}
							iconLabel={RiProfileLine}
							{...register("doc")}
							error={formState.errors.doc}
							maxLength={pessoa === 'fisica' ? 14 : 18}
							onChange={(e: any) => {
								pessoa === 'fisica' ? handleMaskCpf(e.target.value) : handleMaskCnpj(e.target.value)
							}}
              onBlur={(e: any) =>{
								pessoa === 'juridica' && handleSearchCnpj(e.target.value)
							}}
						/>

						{/* NOME */}
						<Input
							type="text"
							label="Nome"
              iconLabel={RiUser3Line}
							{...register('nome')}
							error={formState.errors.nome}
						/>

						{pessoa === 'juridica' && (
							<>
								<Input
									type="text"
									label="Nome fantasia"
									iconLabel={RiAtLine}
									{...register("nome_fantasia")}
									error={formState.errors.nome_fantasia}
								/>
								<Input
									type="date"
									label="Início das atividades"
									iconLabel={RiAtLine}
									{...register("inicio_atividades")}
									error={formState.errors.email}
								/>
								<Flex flexDir="column">
									<Flex align="center">
										<Icon as={MdList} fontSize="20" mr="2" />
										<Text fontWeight="500">Classificação</Text>
									</Flex>

									<Select
										options={classificacoes}
										{...register("classif")}
										error={formState.errors.classif}
										bg="gray.100"
									/>
								</Flex>

								<Input
									type="text"
									label="Inscrição Estadual"
									iconLabel={RiAtLine}
									{...register("insc_estadual")}
									error={formState.errors.insc_estadual}
								/>
							</>
						)}

						<Input
							type="text"
							label="E-mail"
							iconLabel={RiAtLine}
							{...register("email")}
							error={formState.errors.email}
						/>

						{pessoa === 'fisica' && (
							<>
								<Input
									type="text"
									label="Rg"
									iconLabel={RiProfileLine}
									{...register("rg")}
									error={formState.errors.rg}
								/>
								<Input
									type="date"
									label="Data de Nascimento"
									iconLabel={RiProfileLine}
									{...register("nasc")}
								/>

								<Flex
									flexDir="column"
									pb="4"
								>
									<Flex align="center">
										<Icon as={sexo === 'feminino' ? RiWomenLine : RiMenLine}
											fontSize="18"
											mr="2"
										/>
										<Text fontSize="18">Sexo: </Text>
									</Flex>

									<RadioGroup
										onChange={setSexo}
										value={sexo}
										bg="gray.100"
										borderRadius={8}
										pl="4"
										py="2.5"
									>
										<Stack direction='row' spacing="4">
											<Radio value='masculino'>Masculino</Radio>
											<Radio value='feminino'>Feminino</Radio>
										</Stack>
									</RadioGroup>
								</Flex>
							</>
						)}

						{/* TELEFONES */}
						<Flex flexDir="column">
							<Flex align="center">
								<Icon as={MdOutlineAddIcCall} fontSize="20" mr="2" />
								<Text mr="4">Telefones</Text>

								<Button
									bg="transparent"
									borderColor="blue"
									borderWidth="2"
									color="blue.500"
									p="0"
									_hover={{
										bg: "transparent",
										color: "filter(0.2)",
									}}
									_focus={{
										bg: "transparent"
									}}
									onClick={() => setIsNewTelClienteModal(true)}
								>
									<Icon as={RiAddLine} fontSize="20" mr="1" />
									Novo
								</Button>
							</Flex>

							<Flex pl="2" py="2" flexDir="column">
								<Stack spacing="2">
									{phones.length > 0 ? phones.map((phone, indice) => {
										return (
											<PhoneCadastro
												key={indice}
												phone={phone}
												setPhoneModal={setPhoneModal}
												setIsNewTelClienteModal={setIsNewTelClienteModal}
												phones={phones}
												setPhones={setPhones}
											/>
										)
									}) :
										<Text color="gray.400">Nenhum telefone</Text>
									}
								</Stack>
							</Flex>
						</Flex>

						{/* ENDEREÇOS */}
						<Flex flexDir="column">
							<Flex align="center">
								<Icon as={RiMap2Line} fontSize="20" mr="2" />
								<Text mr="4">Endereços</Text>

								<Button
									bg="transparent"
									borderColor="blue"
									borderWidth="2"
									color="blue.500"
									p="0"
									_hover={{
										bg: "transparent",
										color: "filter(0.2)",
									}}
									_focus={{
										bg: "transparent"
									}}
									onClick={() => setIsNewEndClienteModal(true)}
								>
									<Icon as={RiAddLine} fontSize="20" mr="1" />
									Novo
								</Button>
							</Flex>

							<Flex pl="2" py="2" flexDir="column">
								<Stack spacing="2">
									{enderecos.length > 0 ? enderecos.map((end, indice) => {
										return (
											<EnderecoCadastro
												key={indice}
												endereco={end}
												setEndModal={setEndModal}
												setIsNewEndClienteModal={setIsNewEndClienteModal}
												enderecos={enderecos}
												setEnderecos={setEnderecos}
											/>
										)
									}) :
										<Text color="gray.400">Nenhum endereço</Text>
									}
								</Stack>
							</Flex>
						</Flex>
					</Stack>

					<Flex w="100%" mt="4">
						<Button type="submit" colorScheme="blue" w="100%" h="14">Salvar</Button>
						<Button
							colorScheme="blackAlpha"
							ml="4"
							w="100%"
							h="14"
							onClick={() => history.push('/client')}
						>Cancelar</Button>
					</Flex>
				</Flex>
			</Flex>

			{isNewTelClienteModal && (
				<NewPhoneClienteModal
					isOpen={isNewTelClienteModal}
					onRequestClose={handleCloseNewTelClienteModal}
					setPhones={setPhones}
					phones={phones}
					phoneModal={phoneModal}
					setPhoneModal={setPhoneModal}
				/>
			)}

			{isNewEndClienteModal && (
				<NewEndClienteModal
					isOpen={isNewEndClienteModal}
					onRequestClose={handleCloseNewEndClienteModal}
					setEnderecos={setEnderecos}
					enderecos={enderecos}
					endModal={endModal}
					setEndModal={setEndModal}
				/>
			)}
		</Flex>
	)
}

export default CadClient;
